import { useState } from 'react';
import config from "../config.json";

const STORAGE_KEY = 'appSettings';

// Carga los settings del storage, con fallback al config.json
function loadSettings() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        stations: Array.isArray(parsed.stations) && parsed.stations.length > 0
          ? parsed.stations
          : config.stations,
        primary_station: parsed.primary_station || config.primary_station,
      };
    }
  } catch (_) {}
  return {
    stations: config.stations,
    primary_station: config.primary_station,
  };
}

function Settings() {
  const initialSettings = loadSettings();
  const [stations, setStations] = useState(initialSettings.stations);
  const [primaryStation, setPrimaryStation] = useState(initialSettings.primary_station);
  const [saved, setSaved] = useState(false);

  const allStations = config.stations;

  const handleStationToggle = (station) => {
    setStations((prev) => {
      if (prev.includes(station)) {
        // No permitir desmarcar si sólo queda una
        if (prev.length === 1) return prev;
        const next = prev.filter((s) => s !== station);
        // Si la primaria queda fuera, resetearla a la primera disponible
        if (primaryStation === station) {
          setPrimaryStation(next[0]);
        }
        return next;
      } else {
        return [...prev, station];
      }
    });
    setSaved(false);
  };

  const handlePrimaryChange = (e) => {
    setPrimaryStation(e.target.value);
    setSaved(false);
  };

  const handleSave = () => {
    const settings = { stations, primary_station: primaryStation };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    setSaved(true);
  };

  const handleReset = () => {
    const defaults = {
      stations: config.stations,
      primary_station: config.primary_station,
    };
    setStations(defaults.stations);
    setPrimaryStation(defaults.primary_station);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(defaults));
    setSaved(true);
  };

  // La estación primaria debe estar entre las seleccionadas
  const validPrimary = stations.includes(primaryStation) ? primaryStation : stations[0] || '';

  return (
    <div className="container-fluid px-4 py-5" style={{ maxWidth: 600 }}>

      <h4 className="mb-1 text-white fw-light">
        <i className="bi bi-sliders me-2 text-secondary"></i>Settings
      </h4>
      <p className="text-secondary small mb-4">
        Customise stations and defaults.
      </p>

      {/* --- Stations --- */}
      <div className="mb-4">
        <label className="form-label text-uppercase text-secondary fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.12em' }}>
          Active Stations
        </label>
        <p className="text-secondary small mb-2">
          Select which stations are fetched and displayed in Dashboard &amp; Plots.
        </p>
        <div className="d-flex flex-wrap gap-2">
          {allStations.map((station) => {
            const checked = stations.includes(station);
            return (
              <div
                key={station}
                onClick={() => handleStationToggle(station)}
                className="d-flex align-items-center gap-2 px-3 py-2 rounded-2"
                role="checkbox"
                aria-checked={checked}
                style={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  border: checked ? '1px solid #6c9cbf' : '1px solid #444',
                  background: checked ? 'rgba(108,156,191,0.12)' : 'rgba(255,255,255,0.03)',
                  color: checked ? '#a8ccdf' : '#888',
                  transition: 'all 0.15s ease',
                  minWidth: 110,
                }}
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    border: checked ? '2px solid #6c9cbf' : '2px solid #555',
                    background: checked ? '#6c9cbf' : 'transparent',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.15s ease',
                  }}
                >
                  {checked && (
                    <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                      <path d="M1 3.5L3.5 6L8 1" stroke="#0d1117" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <span className="text-capitalize" style={{ fontSize: '0.875rem' }}>{station}</span>
              </div>
            );
          })}
        </div>
        {stations.length === 0 && (
          <p className="text-danger small mt-2">Select at least one station.</p>
        )}
      </div>

      {/* --- Primary Station --- */}
      <div className="mb-5">
        <label
          htmlFor="primary-station-select"
          className="form-label text-uppercase text-secondary fw-semibold"
          style={{ fontSize: '0.7rem', letterSpacing: '0.12em' }}
        >
          Primary Station
        </label>
        <p className="text-secondary small mb-2">
          The main data source for the Dashboard.
        </p>
        <select
          id="primary-station-select"
          className="form-select bg-dark text-white border-secondary"
          style={{ maxWidth: 250 }}
          value={validPrimary}
          onChange={handlePrimaryChange}
        >
          {stations.map((station) => (
            <option key={station} value={station}>
              {station}
            </option>
          ))}
        </select>
      </div>

      {/* --- Actions --- */}
      <div className="d-flex align-items-center gap-3">
        
        <button
          className="btn btn-primary px-4"
          onClick={handleSave}
          disabled={stations.length === 0}
        >
          <i className="bi bi-floppy me-2"></i>Save
        </button>
        <button
          className="btn btn-outline-secondary px-3"
          onClick={handleReset}
        >
          <i className="bi bi-arrow-counterclockwise me-2"></i>Reset to defaults
        </button>
        {saved && (
          <span className="text-success small">
            <i className="bi bi-check-circle me-1"></i>Saved
          </span>
        )}
      </div>
      <p className="text-secondary small mb-2 mt-2">
        Please reload the page after changing the settings.
      </p>
    </div>
  );
}

export default Settings;